import * as React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import { type ThemeOptions } from "@mui/material/styles";

type ConfirmOptions = {
  title?: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  okOnly?: boolean; // default: true
  catchOnCancel?: boolean; // default: false
  primaryColor?: string;
  secondaryColor?: string;
  themeOverrides?: ThemeOptions;
};

type ConfirmFn = (opts?: ConfirmOptions) => Promise<void>;

const ConfirmCtx = React.createContext<ConfirmFn>(() => Promise.reject());
export function useConfirm() {
  return React.useContext(ConfirmCtx);
}

const DEFAULTS: Required<Pick<ConfirmOptions, "okOnly">> &
  Pick<ConfirmOptions, "title" | "description" | "confirmText" | "cancelText"> =
  {
    okOnly: true,
    title: "Confirm",
    description: "",
    confirmText: "OK",
    cancelText: "Cancel",
  };

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [opts, setOpts] = React.useState<ConfirmOptions | null>(null);
  const waiter = React.useRef<{
    resolve: () => void;
    reject: () => void;
  } | null>(null);

  const confirm: ConfirmFn = React.useCallback((o = {}) => {
    setOpts({ ...DEFAULTS, ...o }); // okOnly defaults to true, text = "OK"
    return new Promise<void>((resolve, reject) => {
      waiter.current = { resolve, reject };
    });
  }, []);

  const close = () => {
    if (opts?.catchOnCancel) waiter.current?.reject();
    setOpts(null);
  };

  const submit = () => {
    waiter.current?.resolve();
    setOpts(null);
  };

  // const theme = React.useMemo(
  //   () =>
  //     createTheme({
  //       palette: {
  //         primary: { main: opts?.primaryColor ?? PrimaryColor },
  //         secondary: { main: opts?.secondaryColor ?? SecondaryColor },
  //       },
  //       ...(opts?.themeOverrides ?? {}),
  //     }),
  //   [opts?.primaryColor, opts?.secondaryColor, opts?.themeOverrides]
  // );

  const onKeyDown: React.KeyboardEventHandler = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submit(); // Enter confirms
    }
  };

  // Render nothing when closed (prevents brief re-render with defaults)
  const dialog = opts ? (
    <Dialog
      open
      onClose={close} // Esc/backdrop => cancel
      onKeyDown={onKeyDown}
      aria-labelledby="confirm-title"
      aria-describedby="confirm-desc"
    >
      <DialogTitle id="confirm-title">{opts.title}</DialogTitle>
      <DialogContent>
        <DialogContentText id="confirm-desc" sx={{ whiteSpace: "pre-line" }}>
          {opts.description || "Are you sure?"}
        </DialogContentText>
      </DialogContent>

      {opts.okOnly ? (
        <DialogActions sx={{ justifyContent: "center" }}>
          <Button color="primary" onClick={submit} autoFocus>
            <strong>{opts.confirmText}</strong>
          </Button>
        </DialogActions>
      ) : (
        <DialogActions sx={{ justifyContent: "space-between", px: 3 }}>
          <Button color="secondary" onClick={close}>
            <strong>{opts.cancelText}</strong>
          </Button>
          <Button color="primary" onClick={submit} autoFocus>
            <strong>{opts.confirmText}</strong>
          </Button>
        </DialogActions>
      )}
    </Dialog>
  ) : null;

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      {dialog}
    </ConfirmCtx.Provider>
  );
}
